// Minimal client-side integration with the Plaid Link SDK. The official
// react-plaid-link wrapper would add another dependency for the same surface;
// loading link-initialize.js directly from Plaid's CDN keeps the diff small
// and matches what `react-plaid-link` does under the hood.

const PLAID_SCRIPT_SRC = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";

interface PlaidInstitution {
  name: string;
  institution_id: string;
}

// One entry of the `metadata.accounts` array returned to onSuccess. With
// Account Select enabled (FIN-31) this contains only the accounts the user
// checked in Link's UI; without it, every account in the institution
// appears here. The fields match the Plaid Link SDK shape; only `id` is
// required by the API, the rest are forwarded as-is for diagnostics.
export interface PlaidLinkAccountMetadata {
  id: string;
  name: string;
  mask: string | null;
  type: string;
  subtype: string | null;
  verification_status?: string | null;
}

export interface PlaidLinkOnSuccessMetadata {
  institution: PlaidInstitution | null;
  link_session_id: string;
  accounts: PlaidLinkAccountMetadata[];
}

export interface PlaidLinkOnExitMetadata {
  status: string | null;
  link_session_id: string;
  request_id?: string;
}

export interface PlaidLinkError {
  error_type: string;
  error_code: string;
  error_message: string;
  display_message: string | null;
}

interface PlaidLinkHandler {
  open: () => void;
  exit: (options?: { force?: boolean }) => void;
  destroy: () => void;
}

interface PlaidLinkCreateOptions {
  token: string;
  onSuccess: (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => void;
  onExit: (
    error: PlaidLinkError | null,
    metadata: PlaidLinkOnExitMetadata,
  ) => void;
  onLoad?: () => void;
}

interface PlaidLinkGlobal {
  create: (options: PlaidLinkCreateOptions) => PlaidLinkHandler;
}

declare global {
  interface Window {
    Plaid?: PlaidLinkGlobal;
  }
}

let scriptPromise: Promise<PlaidLinkGlobal> | null = null;

export function loadPlaidLink(): Promise<PlaidLinkGlobal> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Plaid Link can only load in the browser"));
  }
  if (window.Plaid) return Promise.resolve(window.Plaid);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<PlaidLinkGlobal>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${PLAID_SCRIPT_SRC}"]`,
    );
    const onload = () => {
      if (window.Plaid) resolve(window.Plaid);
      else reject(new Error("Plaid Link script loaded but window.Plaid is undefined"));
    };
    const onerror = () => {
      scriptPromise = null;
      reject(new Error("Failed to load Plaid Link script"));
    };
    if (existing) {
      existing.addEventListener("load", onload, { once: true });
      existing.addEventListener("error", onerror, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = PLAID_SCRIPT_SRC;
    script.async = true;
    script.addEventListener("load", onload, { once: true });
    script.addEventListener("error", onerror, { once: true });
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export type {
  PlaidLinkCreateOptions,
  PlaidLinkGlobal,
  PlaidLinkHandler,
};
